/* Pureverr loop video controller.
   One shared controller for every [data-pv-loop] media unit on the page: near-viewport warming,
   play-when-visible, pause-when-offscreen, a page-level motion preference shared by every unit,
   reduced-motion handling, and Theme Editor re-initialisation. No dependencies. */
(function () {
  'use strict';
  if (window.PureverrLoopVideo) { return; }

  var KEY = 'pureverr:motion';
  var rm = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var stored = null;
  try { stored = window.sessionStorage.getItem(KEY); } catch (e) { stored = null; }
  /* Motion is off by default only when the OS asks for reduced motion; an explicit choice made on this
     page (this tab session) wins either way. */
  var paused = stored === 'paused' || (stored !== 'playing' && !!(rm && rm.matches));
  var units = [];
  var observersReady = false;
  var warmIO = null;
  var playIO = null;

  function within(video, f) {
    var r = video.getBoundingClientRect();
    var h = window.innerHeight || document.documentElement.clientHeight;
    return r.bottom > -f * h && r.top < (1 + f) * h;
  }
  function near(video) { return within(video, 0.25); }
  function ahead(video) { return within(video, 1.75); }
  function sync(u) {
    var off = paused || u.denied === true;
    u.fig.setAttribute('data-motion', off ? 'paused' : 'playing');
    if (u.btn) { u.btn.setAttribute('aria-pressed', off ? 'true' : 'false'); }
  }
  function warm(u) {
    if (u.warm || paused) { return; }
    u.warm = true;
    u.video.preload = 'auto';
    try { u.video.load(); } catch (e) { /* ignore */ }
  }
  function play(u) {
    var v = u.video;
    if (paused) { return; }
    v.muted = true;
    v.defaultMuted = true;
    if (!v.paused && !v.ended) { return; }
    var p = null;
    try { p = v.play(); } catch (e) { p = null; }
    if (p && typeof p.then === 'function') {
      p.then(function () { u.denied = false; sync(u); })
       .catch(function (e) { if (e && e.name === 'AbortError') { return; } u.denied = true; sync(u); });
    }
  }
  function pause(u) {
    if (!u.video.paused) { u.video.pause(); }
  }
  /* Progressive disclosure of the motion control: it is usable from the first frame; this only turns on
     the visual emphasis. Sticky for the page session; never reverted. */
  function reveal(u) {
    if (u.revealed) { return; }
    u.revealed = true;
    u.fig.setAttribute('data-control', 'revealed');
    if (u.onTime) { u.video.removeEventListener('timeupdate', u.onTime); u.onTime = null; }
  }
  function revealAll() {
    for (var i = 0; i < units.length; i++) { reveal(units[i]); }
  }
  function setPaused(next, persist) {
    paused = !!next;
    if (persist) { stored = paused ? 'paused' : 'playing'; try { window.sessionStorage.setItem(KEY, stored); } catch (e) { /* ignore */ } }
    for (var i = 0; i < units.length; i++) {
      var u = units[i];
      if (paused) { pause(u); } else { if (ahead(u.video)) { warm(u); } if (near(u.video)) { play(u); } }
      sync(u);
    }
  }
  function observe(u) {
    if (!warmIO) { warm(u); play(u); return; }
    warmIO.observe(u.video);
    playIO.observe(u.video);
  }
  function setup(fig) {
    if (fig.__pvUnit) { return; }
    var video = fig.querySelector('video');
    if (!video) { return; }
    var u = { fig: fig, video: video, btn: fig.querySelector('[data-pv-motion]'), warm: false, denied: false, loops: 0, lastT: null, revealed: false, onTime: null };
    fig.__pvUnit = u;
    video.__pvUnit = u;
    video.muted = true;
    video.defaultMuted = true;
    units.push(u);
    video.addEventListener('playing', function () { fig.classList.add('is-playing'); u.denied = false; sync(u); });
    /* Count native loop wraps (time falls from the second half back into the first) while actually playing;
       offscreen and hidden-tab pauses stop timeupdate, so loops never accrue unwatched. */
    u.onTime = function () {
      var t = video.currentTime, d = video.duration;
      if (video.paused || video.seeking || !(d > 0)) { return; }
      if (u.lastT !== null && u.lastT > d * 0.5 && t < d * 0.5) {
        u.loops += 1;
        if (u.loops >= 2) { reveal(u); return; }
      }
      u.lastT = t;
    };
    video.addEventListener('timeupdate', u.onTime);
    if (fig.hasAttribute('data-pv-no-poster')) {
      video.addEventListener('loadedmetadata', function () { if (video.videoWidth && video.videoHeight) { fig.style.setProperty('--pv-aspect', video.videoWidth + ' / ' + video.videoHeight); } }, { once: true });
    }
    if (u.btn) {
      u.btn.addEventListener('click', function () {
        revealAll();
        if (paused || u.denied) {
          u.denied = false;
          setPaused(false, true);
          warm(u);
          play(u); /* inside the user gesture, so a denied autoplay can now start */
        } else {
          setPaused(true, true);
        }
      });
    }
    sync(u);
    if (stored !== null) { reveal(u); } /* the control was already used in this tab session */
    if (observersReady) { observe(u); }
  }
  function teardown(fig) {
    var u = fig.__pvUnit;
    if (!u) { return; }
    if (warmIO) { warmIO.unobserve(u.video); playIO.unobserve(u.video); }
    pause(u);
    units = units.filter(function (x) { return x !== u; });
    fig.__pvUnit = null;
  }
  function init(root) {
    var figs = (root || document).querySelectorAll('[data-pv-loop]');
    for (var i = 0; i < figs.length; i++) { setup(figs[i]); }
  }
  function startObservers() {
    if (observersReady) { return; }
    observersReady = true;
    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < units.length; i++) { warm(units[i]); play(units[i]); }
      return;
    }
    warmIO = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var u = entries[i].target.__pvUnit;
        if (!u || !entries[i].isIntersecting) { continue; }
        warm(u);
        if (u.warm) { warmIO.unobserve(entries[i].target); }
      }
    }, { rootMargin: '175% 0px 175% 0px' });
    playIO = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var u = entries[i].target.__pvUnit;
        if (!u) { continue; }
        if (entries[i].isIntersecting) { warm(u); play(u); } else { pause(u); }
      }
    }, { rootMargin: '25% 0px 25% 0px', threshold: 0.01 });
    for (var j = 0; j < units.length; j++) { observe(units[j]); }
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      for (var i = 0; i < units.length; i++) { pause(units[i]); }
      return;
    }
    for (var j = 0; j < units.length; j++) {
      if (!paused && units[j].warm && near(units[j].video)) { play(units[j]); }
    }
  });
  if (rm && typeof rm.addEventListener === 'function') {
    rm.addEventListener('change', function (e) {
      if (e.matches && stored !== 'playing') { setPaused(true, false); }
    });
  }
  /* Shopify Theme Editor: sections are re-rendered in place. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var figs = e.target.querySelectorAll('[data-pv-loop]');
    for (var i = 0; i < figs.length; i++) { teardown(figs[i]); }
  });

  window.PureverrLoopVideo = {
    init: init,
    setPaused: setPaused,
    isPaused: function () { return paused; },
    state: function () { return units.map(function (u) { return { loops: u.loops, revealed: u.revealed, playing: !u.video.paused }; }); },
    units: function () { return units.length; }
  };

  /* Buttons and state are live as soon as this deferred script runs; observers (and therefore any
     network activity) start only after the page's own load event, so the media never competes with
     the hero, fonts or cart code. */
  init(document);
  var start = function () { window.setTimeout(startObservers, 300); };
  if (document.readyState === 'complete') { start(); } else { window.addEventListener('load', start, { once: true }); }
})();
